import httpClient from '../../services/httpClient';
import { Product, ProductListResponse, ProductResponse, HomeResponse } from './types';

export interface ProductError {
  message: string;
  code?: string;
  status?: number;
}

// Optional filters matching backend BaseParams (query binder is case-insensitive)
export interface GameListFilters {
  name?: string;
  platform?: string;
  platforms?: string[];
  genres?: string[];
  preOrder?: boolean;
  brandNew?: boolean;
  salesLeader?: boolean;
  discounted?: boolean;
  lowestPrice?: boolean;
  topMetacritic?: boolean;
  online?: boolean;
  multiplayer?: boolean;
  sharing?: boolean;
  priceMin?: number;
  priceMax?: number;
  sortBy?: 'price' | 'name' | 'dateRelease';
  sortDesc?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

// Helpers to normalize various response shapes
const getNested = (obj: any, path: string): any => {
  return path.split('.').reduce((acc: any, key: string) => (acc == null ? undefined : acc[key]), obj);
};

const pickArray = <T = unknown>(obj: any, paths: string[]): T[] | undefined => {
  for (const path of paths) {
    const value = getNested(obj, path);
    if (Array.isArray(value)) return value as T[];
  }
  return undefined;
};

const pickNumber = (obj: any, paths: string[], fallback: number): number => {
  for (const path of paths) {
    const value = getNested(obj, path);
    if (typeof value === 'number') return value;
  }
  return fallback;
};

export const productService = {
  // Fetch all products
  fetchProducts: async (filters?: Partial<GameListFilters>): Promise<ProductListResponse> => {
    try {

      const { data } = await httpClient.get('/api/game', { params: filters });

      const {
        products = [],
        total = products.length,
        page = filters?.pageNumber ?? 1,
        limit = filters?.pageSize ?? products.length,
      } = data as Partial<ProductListResponse>;

      
      return { products, total, page, limit };
      
    } catch (error) {
      console.error('Error fetching products:', error);
      
      if (error instanceof Error) {
        throw {
          message: error.message,
          code: 'PRODUCTS_FETCH_ERROR',
          status: 500
        } as ProductError;
      }
      
      throw {
        message: 'Failed to load products. Please try again.',
        code: 'UNKNOWN_ERROR',
        status: 500
      } as ProductError;
    }
  },

  fetchHome: async (): Promise<HomeResponse> => {
    const { data } = await httpClient.get<HomeResponse>('/api/home');
    return {
      newReleases: data.newReleases ?? data.brandNew ?? [],
      brandNew: data.brandNew ?? [],
      salesLeaders: data.salesLeaders ?? [],
      lowestPrices: data.lowestPrices ?? [],
      topMetacritic: data.topMetacritic ?? [],
      preOrders: data.preOrders ?? [],
      services: data.services ?? [],
      serviceGroups: (data.serviceGroups ?? []).map(g => ({
        category: g.category,
        services: g.services ?? [],
      })),
    };
  },

  // Fetch single product by ID
  fetchProduct: async (id: number): Promise<ProductResponse> => {
    try {
      // In a real implementation, this would be:
      const response = await httpClient.get(`/api/game/${id}`);
      const data = response.data;

      // Normalize to { product }
      let product: Product | undefined = data?.product ?? data;

      // Unwrap nested $values for DLC list if present
      if (product && (product as any).listDLC?.$values && Array.isArray((product as any).listDLC.$values)) {
        product = {
          ...(product as any),
          listDLC: (product as any).listDLC.$values,
        } as Product;
      }
      if (product && (product as any).purchaseOffers?.$values && Array.isArray((product as any).purchaseOffers.$values)) {
        product = { ...(product as any), purchaseOffers: (product as any).purchaseOffers.$values } as Product;
      }

      return { product } as ProductResponse;

    } catch (error) {
      console.error('Error fetching product:', error);
      
      if (error instanceof Error) {
        throw {
          message: error.message,
          code: 'PRODUCT_FETCH_ERROR',
          status: error.message.includes('not found') ? 404 : 500
        } as ProductError;
      }
      
      throw {
        message: 'Failed to load product details. Please try again.',
        code: 'UNKNOWN_ERROR',
        status: 500
      } as ProductError;
    }
  }
};
